// @tier: PREMIUM — Page notifications admin uniquement
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  CheckCircle,
  TrendingUp,
  AlertTriangle,
  Search,
  Filter,
  RefreshCw,
  Trash2,
  MoreHorizontal,
  CheckCheck,
  Inbox,
  Clock,
  Megaphone,
  History,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdownMenu';
import { StatusBadge } from '../components/ui/StatusBadge';
import PageError from '../components/PageError';
import useNotifications, {
  getNotificationType,
  getNotificationIcon,
  getNotificationColor,
  formatTimestamp,
} from '../hooks/trNotifications';
import { Button } from '../components/ui/button';
import { getPriorityConfig } from '../utils/notificationPriority';

const RECIPIENT_LABELS = { parent: 'Parent', driver: 'Chauffeur', admin: 'Admin' };
const getRecipientLabel = (recipientType) => RECIPIENT_LABELS[recipientType] ?? recipientType;

const NotificationsEnhanced = () => {
  const navigate = useNavigate();
  const {
    filteredNotifications,
    stats,
    unreadCount,
    loading,
    error,
    filter,
    setFilter,
    recipientTypeFilter,
    setRecipientTypeFilter,
    priorityFilter,
    setPriorityFilter,
    searchTerm,
    setSearchTerm,
    selectedNotifications,
    loadNotifications,
    loadStatistics,
    deleteNotification,
    deleteSelected,
    markAsRead,
    markAllAsRead,
    toggleSelection,
    deselectAll,
  } = useNotifications();

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
          <p className="text-gray-600 mt-1">Notifications envoyées aux parents, chauffeurs et admins</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {unreadCount > 0 && (
            <Button variant="outline" onClick={markAllAsRead}>
              <CheckCheck className="w-4 h-4" />
              Marquer tout comme lu
            </Button>
          )}
          <Button variant="outline" onClick={() => navigate('/admin/notifications/sent')}>
            <History className="w-4 h-4" />
            Notifications envoyées
          </Button>
          <Button onClick={() => navigate('/admin/notifications/new')}>
            <Megaphone className="w-4 h-4" />
            Nouvelle annonce
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { icon: Inbox, bg: 'bg-blue-100', text: 'text-blue-600', label: 'Total', value: stats.total },
          { icon: Bell, bg: 'bg-orange-100', text: 'text-orange-600', label: 'Non lues', value: unreadCount },
          {
            icon: AlertTriangle,
            bg: 'bg-red-100',
            text: 'text-red-600',
            label: 'Urgentes',
            value: stats.byPriority.urgent,
          },
          {
            icon: TrendingUp,
            bg: 'bg-purple-100',
            text: 'text-purple-600',
            label: 'Taux de lecture',
            value: `${stats.readRate}%`,
          },
        ].map(({ icon: Icon, bg, text, label, value }) => (
          <div
            key={label}
            className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 flex items-center space-x-4"
          >
            <div className={`p-3 ${bg} rounded-lg ${text}`}>
              <Icon size={24} />
            </div>
            <div>
              <p className="text-sm text-gray-500">{label}</p>
              <p className="text-2xl font-bold text-gray-900">{value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filtres */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Rechercher une notification..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filtres:</span>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              {[
                { value: 'all', label: 'Toutes', active: 'bg-blue-100 text-blue-800' },
                { value: 'unread', label: 'Non lues', active: 'bg-orange-100 text-orange-800' },
                { value: 'read', label: 'Lues', active: 'bg-gray-200 text-gray-800' },
              ].map(({ value, label, active }) => (
                <button
                  key={value}
                  onClick={() => setFilter(value)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    filter === value ? active : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <select
              value={recipientTypeFilter}
              onChange={(e) => setRecipientTypeFilter(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="all">Tous les destinataires</option>
              <option value="parent">Parents</option>
              <option value="driver">Chauffeurs</option>
              <option value="admin">Admins</option>
            </select>
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <option value="all">Toutes priorités</option>
              <option value="urgent">Urgente</option>
              <option value="high">Haute</option>
              <option value="normal">Normale</option>
              <option value="low">Basse</option>
            </select>
            <button
              onClick={() => {
                loadNotifications();
                loadStatistics();
              }}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Actualiser"
            >
              <RefreshCw size={14} />
            </button>
          </div>
        </div>

        {selectedNotifications.length > 0 && (
          <div className="mt-4 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-4">
            <span className="text-sm font-medium text-blue-900">
              {selectedNotifications.length} notification(s) sélectionnée(s)
            </span>
            <div className="flex items-center space-x-2">
              <button
                onClick={deleteSelected}
                className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 flex items-center gap-1.5"
              >
                <Trash2 className="w-4 h-4" />
                Supprimer
              </button>
              <button
                onClick={deselectAll}
                className="px-3 py-1.5 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700"
              >
                Annuler
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {error && <PageError error={error} onRetry={loadNotifications} variant="inline" />}
        {!error && (
          <>
            {/* Vue cartes — mobile uniquement */}
            <div className="md:hidden divide-y divide-gray-100">
              {loading ? (
                <div className="p-8 text-center text-gray-500">Chargement...</div>
              ) : filteredNotifications.length === 0 ? (
                <div className="p-8 text-center text-gray-500">Aucune notification trouvée</div>
              ) : (
                filteredNotifications.map((notification) => {
                  const type = getNotificationType(notification.notification_type);
                  const Icon = getNotificationIcon(type);
                  const isRead = notification.read_at !== null;
                  const priority = getPriorityConfig(notification.priority);
                  return (
                    <div key={notification.id} className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className={`p-2 rounded-lg border shrink-0 ${getNotificationColor(type)}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 flex items-center gap-2">
                            {notification.title}
                            {!isRead && <span className="w-2 h-2 bg-blue-600 rounded-full shrink-0" />}
                          </p>
                          <p className="text-xs text-gray-500 truncate">{notification.message}</p>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              aria-label="Actions"
                              className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 shrink-0"
                            >
                              <MoreHorizontal size={16} />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            {!isRead && (
                              <DropdownMenuItem onClick={() => markAsRead(notification.id)}>
                                <CheckCircle size={14} className="mr-2" />
                                Marquer comme lue
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => deleteNotification(notification.id)}>
                              <Trash2 size={14} className="mr-2" />
                              Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      <div className="mt-2 ml-[52px] flex flex-wrap items-center gap-2 text-xs text-gray-500">
                        <span className="inline-flex items-center gap-1">
                          <Clock className="h-3 w-3" /> {formatTimestamp(notification.created_at)}
                        </span>
                        <span className="px-2 py-0.5 bg-gray-100 rounded-full">
                          {getRecipientLabel(notification.recipient_type)}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full ${priority.bg} ${priority.text}`}>
                          {priority.label}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Tableau — desktop uniquement */}
            <div className="overflow-x-auto hidden md:block">
              <table className="min-w-[900px] w-full table-fixed">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 w-10">
                      <span className="sr-only">Sélection</span>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Notification
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Destinataire
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Priorité
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-4 py-3 w-16 whitespace-nowrap text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                        Chargement...
                      </td>
                    </tr>
                  ) : filteredNotifications.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center">
                        <Bell className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-1">Aucune notification trouvée</h3>
                        <p className="text-sm text-gray-500">
                          {searchTerm || filter !== 'all'
                            ? 'Aucune notification ne correspond aux critères de recherche.'
                            : "Aucune notification n'a encore été envoyée."}
                        </p>
                      </td>
                    </tr>
                  ) : (
                    filteredNotifications.map((notification) => {
                      const type = getNotificationType(notification.notification_type);
                      const Icon = getNotificationIcon(type);
                      const isRead = notification.read_at !== null;
                      const isSelected = selectedNotifications.includes(notification.id);
                      const priority = getPriorityConfig(notification.priority);
                      return (
                        <tr key={notification.id} className={`hover:bg-gray-50 ${isSelected ? 'bg-blue-50' : ''}`}>
                          <td className="px-4 py-4">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSelection(notification.id)}
                              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                            />
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-start gap-3">
                              <div className={`p-2 rounded-lg border shrink-0 ${getNotificationColor(type)}`}>
                                <Icon className="w-4 h-4" />
                              </div>
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                                  <span className="truncate">{notification.title}</span>
                                  {!isRead && <span className="w-2 h-2 bg-blue-600 rounded-full shrink-0" />}
                                </div>
                                <div className="text-xs text-gray-500 truncate max-w-[280px]">
                                  {notification.message}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                            {getRecipientLabel(notification.recipient_type)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <span className={`px-2 py-0.5 rounded-full text-xs ${priority.bg} ${priority.text}`}>
                              {priority.label}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                            {formatTimestamp(notification.created_at)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap">
                            <StatusBadge status={isRead ? 'read' : 'unread'} />
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-right">
                            <div className="flex justify-end">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button
                                    aria-label="Actions"
                                    className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                                  >
                                    <MoreHorizontal size={16} />
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-48">
                                  {!isRead && (
                                    <DropdownMenuItem onClick={() => markAsRead(notification.id)}>
                                      <CheckCircle size={14} className="mr-2" />
                                      Marquer comme lue
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem onClick={() => deleteNotification(notification.id)}>
                                    <Trash2 size={14} className="mr-2" />
                                    Supprimer
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default NotificationsEnhanced;
